import React, { useLayoutEffect, useRef, useState } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_worldLow from "@amcharts/amcharts4-geodata/worldLow";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4themes_myTheme from "../../theme/am4themes_myTheme";
import PropTypes from "prop-types";
import countries from '../../utils/countries.json'

am4core.useTheme(am4themes_animated);
am4core.useTheme(am4themes_myTheme);

const convertToImageSeriesData = (data) => {
    let imageSeriesData = data.map((item) => {
        let titleKey = Object.keys(item).find((k) => { return k.toLowerCase().includes('country') })
        let latitudeKey = Object.keys(item).find(k => (k.toLowerCase().includes('latitude')))
        let longitudeKey = Object.keys(item).find(k => (k.toLowerCase().includes('longitude')))
        let countKey = Object.keys(item).find(k => k.toLowerCase().includes('count') && k !== titleKey)
        return {
            title: item[titleKey],
            latitude: item[latitudeKey],
            longitude: item[longitudeKey],
            color: item.color,
            value: item[countKey],
            radius: (item[countKey] / 50) <= 10 ? (item[countKey] / 50) : 10
        }
    })

    let filteredArr = imageSeriesData.reduce((acc, current) => {
        const x = acc.find(item => item.title === current.title);
        if (!x) {
            return acc.concat([current]);
        } else {
            return acc;
        }
    }, []);

    const newImageSeriesData = filteredArr.map(country => {
        let x = imageSeriesData.filter(c => { return country.title == c.title })
        x = x.reduce((acc, item) => {
            return {
                title: country.title,
                latitude: country.latitude,
                longitude: country.longitude,
                color: country.color,
                value: parseInt(acc.value) + parseInt(item.value)
            }
        })

        return x
    })

    return newImageSeriesData

}

const processData = (resultSet) => {
    const { measures, dimensions, timeDimensions } = resultSet.annotation()
    const getKeyName = (arr, attrName) => {
        return Object.keys(arr).find(key =>
            !!arr[key].meta && arr[key].meta['map'] == attrName
        )
    }

    const countryKey = getKeyName(dimensions, 'country')
    const valueKey = getKeyName(measures, 'value')

    let tp = resultSet.tablePivot()
    let updatedTp = tp.map(item => {
        let countryCode = countries.find(c => item[countryKey]?.includes(c.name))?.code
        return {
            name: item[countryKey],
            id: countryCode,
            value: item[valueKey]
        }
    }).filter(item => !!item.id)

    updatedTp = updatedTp.reduce((acc, current) => {
        let index = acc.findIndex(item => item.id == current.id)
        if (index === -1) {
            return acc.concat([current])
        } else {
            acc[index].value = parseInt(acc[index].value) + parseInt(current.value)
            return acc
        }
    }, [])

    return updatedTp

}

const AmMap = ({ data, seriesNames, mapTitle, resultSet }) => {
    const chart = useRef(null);
    const divId = Math.random();

    useLayoutEffect(() => {
        let x = am4core.create(`chartdiv-${divId}`, am4maps.MapChart);
        data.forEach((item, key) => {
            data[key].color = x.colors?.getIndex(key) || "#8aabb0";
        });

        let chartData = processData(resultSet)
        chartData.forEach((item, key) => {
            chartData[key].color = x.colors?.getIndex(key) || "#8aabb0";
        });

        console.log("data", data)

        let title = x.titles.create();
        title.text = mapTitle;
        title.textAlign = "middle";
        // Set map definition
        x.geodata = am4geodata_worldLow;

        // Set projection
        x.projection = new am4maps.projections.Miller();

        // Create map polygon series
        let polygonSeries = x.series.push(new am4maps.MapPolygonSeries());
        polygonSeries.exclude = ["AQ"];
        polygonSeries.useGeodata = true;
        polygonSeries.nonScalingStroke = true;
        polygonSeries.strokeWidth = 0.5;
        polygonSeries.calculateVisualCenter = true;

        polygonSeries.data = chartData

        polygonSeries.heatRules.push({
            property: "fill",
            target: polygonSeries.mapPolygons.template,
            min: x.colors.getIndex(1).brighten(1),
            max: x.colors.getIndex(1).brighten(-0.3),
            logarithmic: true
        });

        let polygonTemplate = polygonSeries.mapPolygons.template;
        polygonTemplate.tooltipText = "{name}: {value}";
        polygonTemplate.nonScalingStroke = true;
        polygonTemplate.strokeWidth = 0.5;

        let imageSeries = x.series.push(new am4maps.MapImageSeries());
        imageSeries.mapImages.template.propertyFields.longitude = "longitude";
        imageSeries.mapImages.template.propertyFields.latitude = "latitude";
        imageSeries.mapImages.template.tooltipText = "{title}: {value}";
        imageSeries.data = chartData // convertToImageSeriesData(data)
        imageSeries.dataFields.value = "value";

        // let circle = imageSeries.mapImages.template.createChild(am4core.Circle);
        // // circle.propertyFields.radius = "radius"
        // circle.propertyFields.fill = "color";
        // circle.fillOpacity = 0.7;
        // circle.tooltipText = "{title}: [bold]{value}[/]";
        // circle.nonScaling = true;

        // imageSeries.heatRules.push({
        //     target: circle,
        //     property: "radius",
        //     min: 2,
        //     max: 30,
        //     dataField: "value",
        // });

        imageSeries.id = "markers";

        let imageTemplate = imageSeries.mapImages.template;
        imageTemplate.nonScaling = true;
        imageTemplate.adapter.add("latitude", function (latitude, target) {
            let polygon = polygonSeries.getPolygonById(
                target.dataItem.dataContext.id
            );
            if (polygon) {
                return polygon.visualLatitude;
            }
            return latitude;
        });

        imageTemplate.adapter.add("longitude", function (longitude, target) {
            let polygon = polygonSeries.getPolygonById(
                target.dataItem.dataContext.id
            );
            if (polygon) {
                return polygon.visualLongitude;
            }
            return longitude;
        });
        x.maxZoomLevel = 1;

        x.logo.disabled = true;
        chart.current = x;

        return () => {
            x.dispose();
        };
    }, [])

    return (
        <>
            <div
                id={`chartdiv-${divId}`}
                style={{ width: "100%", height: "500px" }}
            ></div>
        </>
    );
}

const AmMapOld = ({ data, seriesNames, mapTitle, resultSet }) => {
    const chart = useRef(null);
    const divId = Math.random();

    useLayoutEffect(() => {
        // Create map instance
        let x = am4core.create(`chartdiv-${divId}`, am4maps.MapChart);
        data.forEach((item, key) => {
            data[key].color = x.colors?.getIndex(key) || "#8aabb0";
        });
        // console.log(data);

        let title = x.titles.create();
        title.text = mapTitle;
        title.textAlign = "middle";
        // Set map definition
        x.geodata = am4geodata_worldLow;

        // Set projection
        x.projection = new am4maps.projections.Miller();

        // Create map polygon series
        let polygonSeries = x.series.push(new am4maps.MapPolygonSeries());
        polygonSeries.exclude = ["AQ"];
        polygonSeries.useGeodata = true;
        polygonSeries.nonScalingStroke = true;
        polygonSeries.strokeWidth = 0.5;
        polygonSeries.calculateVisualCenter = true;

        let imageSeries = x.series.push(new am4maps.MapImageSeries());

        // ---- new 
        imageSeries.mapImages.template.propertyFields.longitude = "longitude";
        imageSeries.mapImages.template.propertyFields.latitude = "latitude";
        imageSeries.mapImages.template.tooltipText = "{title}";
        imageSeries.data = convertToImageSeriesData(data)
        // -----

        // imageSeries.data = data;
        imageSeries.dataFields.value = "value";

        let imageTemplate = imageSeries.mapImages.template;
        imageTemplate.nonScaling = true;

        let circle = imageTemplate.createChild(am4core.Circle);
        circle.fillOpacity = 0.7;
        circle.propertyFields.fill = "color";
        circle.tooltipText = "{name}: [bold]{value}[/]";

        imageSeries.heatRules.push({
            target: circle,
            property: "radius",
            min: 4,
            max: 30,
            dataField: "value",
        });

        imageTemplate.adapter.add("latitude", function (latitude, target) {
            let polygon = polygonSeries.getPolygonById(
                target.dataItem.dataContext.id
            );
            if (polygon) {
                return polygon.visualLatitude;
            }
            return latitude;
        });

        imageTemplate.adapter.add("longitude", function (longitude, target) {
            let polygon = polygonSeries.getPolygonById(
                target.dataItem.dataContext.id
            );
            if (polygon) {
                return polygon.visualLongitude;
            }
            return longitude;
        });

        //set data
        // x.data = data;
        //set references for use in div
        x.logo.disabled = true;
        chart.current = x;

        return () => {
            x.dispose();
        };
    }, []);

    return (
        <>
            <div
                id={`chartdiv-${divId}`}
                style={{ width: "100%", height: 500 }}
            ></div>
        </>
    );
}

Map.defaultProps = {
    data: [],
    seriesNames: [],
    mapTitle:
        "[bold font-size: 20]Population of the World in 2011[/]\nsource: Gapminder",
};
Map.PropTypes = {
    data: PropTypes.array,
    seriesNames: PropTypes.array,
    mapTitle: PropTypes.string,
};

export default AmMap